import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { getStock, getStockFacets } from '../services/api';
import { useVehicles } from '../hooks/useVehicles';
import { usePartCatalog } from '../hooks/usePartCatalog';
import { useAuth } from '../context/AuthContext';
import { CITIES } from '../models/cityModel';

// "Tüm ilanlar": every active stock listing, newest first, with guided
// (Marka · Model · Parça · Şehir) and free-text search plus faceted filters.
// Filters live in the URL so a search can be shared (and Google's
// SearchAction /stock?q=… lands here). With no matching stock the page turns
// into a request funnel instead of a dead end.
const FILTER_KEYS = ['q', 'brand', 'model', 'part', 'category', 'city', 'condition', 'sort'];
const SEL = 'w-full px-3 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:border-blue-600 disabled:bg-gray-50 disabled:text-gray-400';
const COND = {
  original:    { label: 'Orijinal', cls: 'bg-green-50 text-green-700 border-green-200' },
  aftermarket: { label: 'Muadil',   cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  used:        { label: 'Çıkma',    cls: 'bg-amber-50 text-amber-700 border-amber-200' },
};
const tl = (v) => `₺${(parseFloat(v) || 0).toLocaleString('tr-TR')}`;
const yearText = (s) => (s.year_from && s.year_to && s.year_from !== s.year_to)
  ? `${s.year_from}–${s.year_to}` : (s.year_from || s.year_to || '');

/* ── One facet block: title, optional search, options with counts ── */
function Facet({ title, options, value, onPick, searchable, labelOf = (v) => v }) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(true);
  const shown = options.filter(o => !term || labelOf(o.value).toLowerCase().includes(term.toLowerCase()));
  if (!options.length && !value) return null;
  return (
    <div className="border-b border-gray-100 py-4 first:pt-0">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between text-sm font-bold text-gray-800 mb-2">
        {title}<span className="text-gray-300 text-xs">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <>
          {searchable && options.length > 6 && (
            <input className="w-full px-3 py-2 mb-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:outline-none focus:border-blue-500"
              placeholder={`${title} ara…`} value={term} onChange={e => setTerm(e.target.value)} />
          )}
          <div className="max-h-56 overflow-y-auto pr-1 space-y-0.5">
            {value && !options.some(o => o.value === value) && (
              <FacetRow label={labelOf(value)} checked onClick={() => onPick('')} />
            )}
            {shown.map(o => (
              <FacetRow key={o.value} label={labelOf(o.value)} count={o.count}
                checked={o.value === value} onClick={() => onPick(o.value === value ? '' : o.value)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function FacetRow({ label, count, checked, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-1.5 py-1.5 rounded-md text-sm text-left transition
        ${checked ? 'bg-blue-50 text-blue-800 font-semibold' : 'text-gray-600 hover:bg-gray-50'}`}>
      <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] flex-shrink-0
        ${checked ? 'bg-blue-700 border-blue-700 text-white' : 'border-gray-300 bg-white'}`}>{checked ? '✓' : ''}</span>
      <span className="flex-1 truncate">{label}</span>
      {count != null && <span className="text-xs text-gray-400 font-semibold tabular-nums">{count.toLocaleString('tr-TR')}</span>}
    </button>
  );
}

/* ── Listing card ── */
function StockCard({ s, onOpen }) {
  const c = COND[s.condition];
  return (
    <button type="button" onClick={() => onOpen(s)}
      className="text-left bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-blue-300 hover:shadow-md transition flex flex-col">
      <div className="aspect-[4/3] bg-gray-100 relative">
        {s.images?.[0] ? (
          <img src={s.images[0]} alt={s.name} loading="lazy" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-4xl text-gray-300">⚙️</div>
        )}
        {c && <span className={`absolute top-2 left-2 text-[11px] font-semibold border px-2 py-0.5 rounded-full ${c.cls}`}>{c.label}</span>}
      </div>
      <div className="p-3 flex flex-col flex-1">
        <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-1.5">{s.name}</h3>
        <div className="flex flex-wrap gap-1 mb-1.5">
          {[s.category, s.subcategory].filter(Boolean).map(t => (
            <span key={t} className="text-[11px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{t}</span>
          ))}
        </div>
        <p className="text-xs text-gray-500 truncate">
          {[[s.car_brand, s.car_model].filter(Boolean).join(' '), yearText(s), s.city].filter(Boolean).join(' • ')}
        </p>
        <div className="mt-auto pt-2 flex items-end justify-between gap-2">
          <span className="text-xs text-gray-400 truncate">
            {s.supplier_name}{s.supplier_verified && <span className="text-green-600 font-semibold" title="Vergi levhası kontrol edildi"> ✓</span>}
          </span>
          <span className="text-base font-bold text-gray-900 whitespace-nowrap">{tl(s.price)}</span>
        </div>
      </div>
    </button>
  );
}

/* ── Where to find codes (collapsible help next to the free-text search) ── */
function CodeHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen(o => !o)} className="text-xs text-blue-700 hover:underline">
        {open ? '▾' : '▸'} OEM, motor kodu veya şasi numarasını nereden bulurum?
      </button>
      {open && (
        <ul className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-gray-500 max-w-3xl">
          <li><strong className="text-gray-700">OEM No</strong> — parçanın üzerinde veya kutusunda yazar; en kesin sonucu verir.</li>
          <li><strong className="text-gray-700">Motor Kodu</strong> — motor bloğunda veya ruhsatta (P5) bulunur.</li>
          <li><strong className="text-gray-700">Şanzıman Kodu</strong> — şanzıman gövdesinde yazar.</li>
          <li><strong className="text-gray-700">Şasi No (VIN)</strong> — ruhsatta veya ön camın alt köşesinde, 17 karakter.</li>
        </ul>
      )}
    </div>
  );
}

/* ── No matching stock → ask suppliers instead (the core of the platform) ── */
function RequestFunnel({ filters, partPath, onRequest, hasFilters }) {
  const chips = [filters.brand, filters.model, filters.part || filters.category, filters.city].filter(Boolean);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
      <div className="w-14 h-14 rounded-2xl bg-blue-50 text-2xl flex items-center justify-center mx-auto mb-4">🔧</div>
      <h2 className="text-lg font-bold text-gray-900 mb-1">Parçanızı tedarikçilerimize soralım</h2>
      <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">
        {hasFilters
          ? 'Bu kriterlerde hazır ilan yok — ama tedarikçilerin çoğu parçayı depodan veya sökümden bulabilir.'
          : 'Aracınızı ve parçanızı yukarıdan seçin ya da doğrudan talep oluşturun; ilgili tedarikçiler size teklif göndersin.'}
      </p>
      {chips.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1.5 mb-5">
          {chips.map(c => <span key={c} className="text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-full">{c}</span>)}
        </div>
      )}
      <button onClick={() => onRequest(partPath)}
        className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-7 py-3 rounded-xl text-sm transition">
        Ücretsiz Teklif Al →
      </button>
      <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 mt-5 text-xs text-gray-400">
        <span>✓ Ücretsiz ve bağlayıcı değil</span>
        <span>✓ Birden fazla teklifi karşılaştırın</span>
        <span>✓ Ödeme teslimata kadar güvencede</span>
      </div>
    </div>
  );
}

export default function StockPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { brands: BRANDS, models: BRAND_MODELS } = useVehicles();
  const partCatalog = usePartCatalog();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(
    () => Object.fromEntries(FILTER_KEYS.map(k => [k, params.get(k) || ''])), [params]);

  const [qInput, setQInput]   = useState(filters.q);
  const [items, setItems]     = useState([]);
  const [count, setCount]     = useState(0);
  const [page, setPage]       = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets]   = useState(null);
  const [modal, setModal]     = useState(null);
  const [photo, setPhoto]     = useState(0);
  const [showFilters, setShowFilters] = useState(false);   // mobile drawer

  useEffect(() => setQInput(filters.q), [filters.q]);

  const apiParams = useCallback(
    (extra = {}) => Object.fromEntries(Object.entries({ ...filters, ...extra }).filter(([, v]) => v !== '')),
    [filters]);

  // New search → page 1 + facet counts.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([getStock(apiParams({ page: 1 })), getStockFacets(apiParams())])
      .then(([r, f]) => {
        if (!alive) return;
        setItems(r.data.results); setCount(r.data.count); setHasMore(r.data.has_more); setPage(1);
        setFacets(f.data);
      })
      .catch(() => { if (alive) { setItems([]); setCount(0); setHasMore(false); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [apiParams]);

  const loadMore = async () => {
    setLoading(true);
    try {
      const r = await getStock(apiParams({ page: page + 1 }));
      setItems(p => [...p, ...r.data.results]); setHasMore(r.data.has_more); setPage(page + 1);
    } finally { setLoading(false); }
  };

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key === 'brand') next.delete('model');            // a model belongs to one brand
    setParams(next, { replace: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const clearAll = () => setParams(new URLSearchParams());
  const active = FILTER_KEYS.filter(k => k !== 'sort' && filters[k]);

  // Every catalog part name → its category/subcategory, for the "Parça" dropdown
  // and for pre-filling the request form.
  const partIndex = useMemo(() => {
    const idx = {};
    Object.entries(partCatalog.parts || {}).forEach(([key, names]) => {
      const [category, subcategory] = key.split('|');
      names.forEach(n => { if (!idx[n]) idx[n] = { category, subcategory }; });
    });
    return idx;
  }, [partCatalog]);
  const partNames = useMemo(() => Object.keys(partIndex).sort((a, b) => a.localeCompare(b, 'tr')), [partIndex]);
  const partPath = filters.part ? { ...partIndex[filters.part], part_name: filters.part } : null;

  // Guided search row (applies on "Ara", like the free-text field).
  const [guide, setGuide] = useState({ brand: filters.brand, model: filters.model, part: filters.part, city: filters.city });
  useEffect(() => setGuide({ brand: filters.brand, model: filters.model, part: filters.part, city: filters.city }),
    [filters.brand, filters.model, filters.part, filters.city]);
  const applyGuide = () => {
    const next = new URLSearchParams(params);
    Object.entries(guide).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    setParams(next);
  };

  const openItem = (s) => { setModal(s); setPhoto(0); };
  const requestFor = (s, path = null) => navigate('/request', {
    state: {
      brand: s?.car_brand || filters.brand,
      model: s?.car_model || filters.model,
      category: s?.category || path?.category || filters.category,
      ...(path?.subcategory && !s ? { subcategory: path.subcategory, part_name: path.part_name } : {}),
    },
  });
  const hasFacetOptions = !!facets && ['brand', 'category', 'city', 'condition'].some(k => facets[k]?.length);

  const sidebar = (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold text-gray-900">Filtreler</h2>
        {active.length > 0 && <button onClick={clearAll} className="text-xs text-blue-700 hover:underline">Temizle</button>}
      </div>
      {facets ? (
        <>
          <Facet title="Marka" searchable options={facets.brand} value={filters.brand} onPick={v => setFilter('brand', v)} />
          {filters.brand && <Facet title="Model" searchable options={facets.model} value={filters.model} onPick={v => setFilter('model', v)} />}
          <Facet title="Kategori" options={facets.category} value={filters.category} onPick={v => setFilter('category', v)} />
          <Facet title="Durum" options={facets.condition} value={filters.condition}
            onPick={v => setFilter('condition', v)} labelOf={v => COND[v]?.label || v} />
          <Facet title="Şehir" searchable options={facets.city} value={filters.city} onPick={v => setFilter('city', v)} />
        </>
      ) : <p className="text-xs text-gray-400">Yükleniyor…</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50"><Navbar />

      {/* ── HEADER + SEARCH ── */}
      <div className="bg-white border-b border-gray-100 pt-24 pb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <button onClick={() => navigate('/')} className="hover:text-blue-700 transition">Ana Sayfa</button>
            <span>›</span><span className="text-gray-600">Tüm İlanlar</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Oto Yedek Parça İlanları — Orijinal, Muadil & Çıkma</h1>
          <p className="text-gray-500 text-sm mb-4 max-w-2xl">
            Onaylı tedarikçilerin stoğundaki parçaları arayın. Bulamazsanız ücretsiz talep oluşturun, tedarikçiler size teklif göndersin.
          </p>
          {/* Guided: Marka · Model · Parça · Şehir */}
          <form onSubmit={e => { e.preventDefault(); applyGuide(); }}
            className="bg-gray-50 border border-gray-100 rounded-2xl p-3 grid grid-cols-2 md:grid-cols-[1fr_1fr_1.3fr_1fr_auto] gap-2">
            <select className={SEL} value={guide.brand} onChange={e => setGuide(g => ({ ...g, brand: e.target.value, model: '' }))}>
              <option value="">Marka</option>
              {BRANDS.map(b => <option key={b}>{b}</option>)}
            </select>
            <select className={SEL} value={guide.model} disabled={!guide.brand} onChange={e => setGuide(g => ({ ...g, model: e.target.value }))}>
              <option value="">Model</option>
              {(BRAND_MODELS[guide.brand] || []).map(m => <option key={m}>{m}</option>)}
            </select>
            <select className={SEL} value={guide.part} onChange={e => setGuide(g => ({ ...g, part: e.target.value }))}>
              <option value="">Parça</option>
              {partNames.map(n => <option key={n}>{n}</option>)}
            </select>
            <select className={SEL} value={guide.city} onChange={e => setGuide(g => ({ ...g, city: e.target.value }))}>
              <option value="">Şehir</option>
              {CITIES.map(c => <option key={c}>{c}</option>)}
            </select>
            <button className="col-span-2 md:col-span-1 bg-blue-700 hover:bg-blue-800 text-white font-semibold px-7 py-3 rounded-xl text-sm transition">
              Ara
            </button>
          </form>

          {/* Free text for people who have a code */}
          <form onSubmit={e => { e.preventDefault(); setFilter('q', qInput.trim()); }}
            className="flex gap-2 mt-3 max-w-3xl">
            <input value={qInput} onChange={e => setQInput(e.target.value)}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-600 transition"
              placeholder="veya kodla arayın: OEM, motor kodu, şanzıman kodu, şasi no…" />
            <button className="border border-blue-200 text-blue-700 hover:bg-blue-50 font-semibold px-5 rounded-xl text-sm transition">Kodla Ara</button>
          </form>
          <CodeHelp />
        </div>
      </div>

      <div className={`max-w-7xl mx-auto px-4 sm:px-6 py-6 grid gap-6 ${hasFacetOptions ? 'lg:grid-cols-[260px_1fr]' : ''}`}>

        {/* ── FILTERS (only when there is something to filter) ── */}
        {hasFacetOptions && <aside className="hidden lg:block">{sidebar}</aside>}

        {/* ── RESULTS ── */}
        <main className="min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {count > 0 && (
              <h2 className="text-base font-bold text-gray-900">{count.toLocaleString('tr-TR')} ilan</h2>
            )}
            <div className="flex flex-wrap gap-1.5">
              {active.map(k => (
                <button key={k} onClick={() => setFilter(k, '')}
                  className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded-full hover:bg-blue-100">
                  {k === 'condition' ? COND[filters[k]]?.label : filters[k]} ✕
                </button>
              ))}
            </div>
            {count > 0 && (
              <div className="ml-auto flex items-center gap-2">
                {hasFacetOptions && (
                  <button onClick={() => setShowFilters(true)}
                    className="lg:hidden text-sm border border-gray-200 bg-white px-3 py-2 rounded-lg">Filtreler{active.length ? ` (${active.length})` : ''}</button>
                )}
                <select value={filters.sort} onChange={e => setFilter('sort', e.target.value)}
                  className="text-sm border border-gray-200 bg-white px-3 py-2 rounded-lg focus:outline-none">
                  <option value="">En yeni</option>
                  <option value="price">Fiyat: düşükten yükseğe</option>
                  <option value="-price">Fiyat: yüksekten düşüğe</option>
                </select>
              </div>
            )}
          </div>

          {loading && !items.length ? (
            <p className="text-center text-gray-400 py-16 text-sm">Yükleniyor…</p>
          ) : items.length === 0 ? (
            <RequestFunnel filters={filters} partPath={partPath} hasFilters={active.length > 0}
              onRequest={(path) => requestFor(null, path)} />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {items.map(s => <StockCard key={s.id} s={s} onOpen={openItem} />)}
              </div>
              {hasMore && (
                <div className="text-center mt-6">
                  <button onClick={loadMore} disabled={loading}
                    className="bg-white border border-gray-200 hover:border-blue-300 text-sm font-semibold text-gray-700 px-6 py-2.5 rounded-xl transition disabled:opacity-60">
                    {loading ? 'Yükleniyor…' : 'Daha fazla göster'}
                  </button>
                </div>
              )}
              <div className="mt-6 bg-blue-700 rounded-2xl p-5 flex flex-col sm:flex-row items-center gap-3 justify-between">
                <div>
                  <p className="text-white text-sm font-bold">Aradığınız parçayı bulamadınız mı?</p>
                  <p className="text-blue-200 text-xs">Talep açın — tedarikçiler size teklif göndersin.</p>
                </div>
                <button onClick={() => requestFor(null, partPath)} className="bg-white text-blue-700 text-sm font-semibold px-5 py-2 rounded-lg hover:bg-blue-50">
                  Talep Oluştur
                </button>
              </div>
            </>
          )}

          {/* Grow the supply side */}
          {user?.role !== 'customer' && user?.role !== 'admin' && (
            <div className="mt-6 border border-dashed border-gray-300 rounded-2xl px-5 py-4 flex flex-col sm:flex-row items-center gap-3 justify-between bg-white">
              <p className="text-sm text-gray-600">
                <strong className="text-gray-800">Tedarikçi misiniz?</strong> Stoğunuzu Excel ile tek seferde yükleyin, alıcılara burada görünün.
              </p>
              <button onClick={() => navigate(user?.role === 'supplier' ? '/dashboard/supplier' : '/register')}
                className="text-sm font-semibold text-blue-700 border border-blue-200 hover:bg-blue-50 px-4 py-2 rounded-lg whitespace-nowrap">
                {user?.role === 'supplier' ? 'Stoğumu Yükle →' : 'Ücretsiz Tedarikçi Ol →'}
              </button>
            </div>
          )}
        </main>
      </div>

      {/* ── MOBILE FILTER DRAWER ── */}
      {showFilters && (
        <div className="fixed inset-0 z-50 bg-black/40 lg:hidden" onClick={() => setShowFilters(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-80 max-w-[85%] bg-gray-50 overflow-y-auto p-4" onClick={e => e.stopPropagation()}>
            <div className="flex justify-end mb-2">
              <button onClick={() => setShowFilters(false)} className="text-sm font-semibold text-blue-700">Kapat ✕</button>
            </div>
            {sidebar}
          </div>
        </div>
      )}

      {/* ── DETAIL MODAL ── */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="grid md:grid-cols-2">
              <div className="bg-gray-100">
                {modal.images?.length ? (
                  <>
                    <img src={modal.images[photo]} alt={modal.name} className="w-full aspect-square object-cover" />
                    {modal.images.length > 1 && (
                      <div className="flex gap-1.5 p-2 overflow-x-auto">
                        {modal.images.map((src, i) => (
                          <button key={src} onClick={() => setPhoto(i)}>
                            <img src={src} alt="" className={`w-14 h-14 object-cover rounded-md border-2 ${i === photo ? 'border-blue-600' : 'border-transparent'}`} />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full aspect-square flex items-center justify-center text-6xl text-gray-300">⚙️</div>
                )}
              </div>
              <div className="p-6 flex flex-col">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="text-lg font-bold text-gray-900 leading-snug">{modal.name}</h3>
                  <button onClick={() => setModal(null)} className="text-gray-300 hover:text-gray-500 text-xl leading-none">✕</button>
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-4">{tl(modal.price)}</p>
                <dl className="text-sm space-y-1.5 mb-4">
                  {[
                    ['Durum', COND[modal.condition]?.label],
                    ['Araç', [modal.car_brand, modal.car_model].filter(Boolean).join(' ')],
                    ['Yıl', yearText(modal)],
                    ['Kategori', [modal.category, modal.subcategory].filter(Boolean).join(' › ')],
                    ['OEM No', modal.oem_no],
                    ['Parça Markası', modal.brand],
                    ['Motor Kodu', modal.motor_code],
                    ['Şanzıman Kodu', modal.gear_code],
                    ['Stok', modal.stock_qty > 1 ? `${modal.stock_qty} adet` : ''],
                    ['Konum', modal.city],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex gap-3"><dt className="w-28 flex-shrink-0 text-gray-400">{k}</dt><dd className="text-gray-800 font-medium">{v}</dd></div>
                  ))}
                </dl>
                {modal.description && <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 mb-4 whitespace-pre-line">{modal.description}</p>}
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-5">
                  <span>🏪 {modal.supplier_name}</span>
                  {modal.supplier_verified && (
                    <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded">✓ Doğrulanmış satıcı</span>
                  )}
                </div>
                <button onClick={() => { const s = modal; setModal(null); requestFor(s); }}
                  className="mt-auto w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 rounded-xl text-sm transition">
                  Bu Parça İçin Teklif İste
                </button>
                <p className="text-xs text-gray-400 text-center mt-2">
                  Talebiniz bu ve diğer onaylı tedarikçilere iletilir; ödeme teslimata kadar güvencede tutulur.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
